import { useEffect, useMemo, useState } from 'react';
import { DeepPartial, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { array, InferType, object } from 'yup';

import { SpaceReadModel, useGetInvestmentsByIdQuery, usePutInvestmentsByInvestmentIdSpacesMutation } from '@client/api';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardTitle,
    EmptyPlaceholder,
    FormSubmitButton,
    Table,
    TableBody,
    TableHead,
    TableHeadCell,
    TableHeadRow,
    useFormAdd
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { InvestmentSpaceItem } from './investment-space-item.component';
import { InvestmentSpaceFormData, InvestmentSpaceItemForm, investmentSpaceSchema } from './investment-space-item.form';

import { CancelRequestModal, SpacesAddModal } from '../../../../../components';

const schema = object({
  spaces: array().of(investmentSpaceSchema).required(),
});

type FormValues = InferType<typeof schema>;
type FormDefaultValues = DeepPartial<FormValues>;

export interface InvestmentSpacesCardProps {
  className?: string;
}

export function InvestmentSpacesCard({ className }: InvestmentSpacesCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const { id } = useParams<{ id: string }>();

  // const pageSize = 20;
  // const [page, setPage] = useState(0);

  const [editing, setEditing] = useState<boolean>(false);

  const { data: investment, isFetching, isLoading } = useGetInvestmentsByIdQuery({ id: id ?? '', }, { skip: id == null });

  const [mutation] = usePutInvestmentsByInvestmentIdSpacesMutation();

  const investmentSpaces = useMemo(() => investment?.spaces ?? [], [investment?.spaces]);
  // const totalCount = investmentSpaces != null && investmentSpaces.length > 0 ? investmentSpaces.length : 0;

  const onSubmit = async (data: FormValues) => {
    if (id == null) return;

    try {
      await mutation({
        investmentId: id,
        body: [...data.spaces],
      }).unwrap();
    } catch (e) {
      return;
    }

    setEditing(false);
  };

  const defaultValues: FormDefaultValues = useMemo(() => ({
    spaces: investmentSpaces.map<InvestmentSpaceFormData>((data) => ({
      id: data.id ?? undefined,
      spaceId: data.space?.id ?? undefined,
      priceId: data.price?.id ?? undefined,
    }))
  }), [investmentSpaces])

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { handleSubmit, formState: { isDirty }, reset, control } = methods;

  /**
   * #########################################
   * Edit Investment Spaces
   */

  const {
    fields: editedInvestmentSpaces,
    items: spaces,
    setItems: setSpaces,
    adding: addingSpaces,
    enableAdding: handleOpenAddSpaces,
    disableAdding: handleCloseAddSpaces,
    setNewItems: handleAddSpaces,
    removeItemByFieldIndex: handleRemoveSpace,
    getItemByFieldIndex: getSpaceByFieldIndex
  } = useFormAdd<InvestmentSpaceFormData, SpaceReadModel>({
    control,
    name: 'spaces',
    mapItemToFormItem: (x): InvestmentSpaceFormData => ({ id: undefined, spaceId: x.id, priceId: undefined }),
    itemEqualsFormItem: (a, x) => a.id === x.spaceId,
    formItemEqualsFormItem: (x, y) => x.spaceId === y.spaceId,
  });

  useEffect(() => {
    setSpaces(investmentSpaces.map((x) => x.space));
    reset(defaultValues);
  }, [defaultValues, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * #########################################
   */

  const hasInvestmentSpaces = investmentSpaces != null && investmentSpaces.length <= 0;
  const hasEditedInvestmentSpaces = editedInvestmentSpaces != null && editedInvestmentSpaces.length <= 0

  const isListEmpty = useMemo(() => !editing ? hasInvestmentSpaces : hasEditedInvestmentSpaces, [editing, hasInvestmentSpaces, hasEditedInvestmentSpaces]);

  const handleOpenEdit = () => {
    setEditing(true);
    // setPage(0);
  };

  const [cancelingRequest, setCancelingRequest] = useState<boolean>(false);

  const checkCancelRequest = () => {
    if (isDirty) {
      setCancelingRequest(true);
    } else {
      setEditing(false);
    }
  };

  const handleConfirmCancelRequest = () => {
    setEditing(false);
    setCancelingRequest(false);
  };

  const handleCancelCancelRequest = () => {
    setCancelingRequest(false);
  };

  return (
    <>
      <Card className={className}>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <CardHeader>
              <CardTitle className="flex-1" variant="dense">
                {tGlobal('entity.investmentSpace', { context: 'plural' })}
              </CardTitle>
              <CardActions>
                {!editing ? (
                  <Button
                    color="primary-dark"
                    disabledVariant="dark"
                    size="sm"
                    onClick={handleOpenEdit}
                    disabled={isFetching || id == null || investment == null}
                  >
                    {tCommon('edit')}
                  </Button>
                ) : (
                  <>
                    <Button size="sm" color="primary-dark" disabled={isFetching} onClick={handleOpenAddSpaces}>
                      {tCommon('add')}
                    </Button>
                    <FormSubmitButton type="submit" size="sm" disabled={isFetching}>
                      {tCommon('save')}
                    </FormSubmitButton>
                    <Button color="secondary-light" size="sm" onClick={checkCancelRequest}>
                      {tCommon('cancel')}
                    </Button>
                  </>
                )}
              </CardActions>
            </CardHeader>

            <CardContent variant="fill">
              <Table className="overflow-hidden w-full mb-4" size="xs" layout="fixed">
                {!isLoading && isListEmpty && (
                  <caption className="px-4 py-4 [caption-side:bottom]">
                    <EmptyPlaceholder>
                      {tCommon('noEntitySpecified', {
                        entity: tGlobal('entity.investmentSpace', { context: 'plural' }).toLowerCase(),
                      })}
                    </EmptyPlaceholder>
                  </caption>
                )}

                <TableHead>
                  <TableHeadRow>
                    <TableHeadCell className="w-[100%]">{tGlobal('entity.investmentSpace', { context: 'short' })}</TableHeadCell>
                  </TableHeadRow>
                </TableHead>

                <TableBody>
                  {isLoading &&
                    Array.from(Array(3).keys()).map((x) => <InvestmentSpaceItem key={x} isFetching />)}

                  {!isLoading &&
                    !editing &&
                    investmentSpaces?.map((space) => (
                      <InvestmentSpaceItem
                        key={space.id}
                        investmentSpace={space}
                        isFetching={isFetching}
                      />
                    ))}

                  {!isLoading &&
                    editing &&
                    editedInvestmentSpaces?.map((field, index) => (
                      <InvestmentSpaceItemForm
                        key={field.id}
                        fieldIndex={index}
                        space={getSpaceByFieldIndex(index)}
                        isFetching={isFetching}
                        onRemove={handleRemoveSpace}
                      />
                    ))}
                </TableBody>
              </Table>

              {/* {!editing && <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />} */}
            </CardContent>
          </form>
        </FormProvider>
      </Card>

      <CancelRequestModal
        open={cancelingRequest}
        onConfirm={handleConfirmCancelRequest}
        onCancel={handleCancelCancelRequest}
      />

      {addingSpaces && (
        <SpacesAddModal
          open={addingSpaces}
          duration={investment?.duration}
          selected={spaces}
          onClose={handleCloseAddSpaces}
          onSubmit={handleAddSpaces}
        />
      )}
    </>
  );
}
